import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, LogOut, ChevronDown, Settings, FileText, Package, MessageCircle, ShoppingCart, ClipboardList, Heart, Briefcase, Building2, Car, MapPin, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import NotificationBell from '../NotificationBell';
import { getUserAvatarUrl } from '../../utils/userAvatar';
import { formatUserRoleLabel } from '../../utils/userRole';

const MobileHeader = ({ data }) => {
  const {
    bannerHeight, navbarVisible,
    logoUrl, isDefaultLogo, siteName,
    selectedProvince, setSelectedProvince, provinces,
    navItems, isActive, user, unreadMessages,
    mobileMenuOpen, setMobileMenuOpen,
    handleLogout, handleLogin,
  } = data;

  const LogoImg = ({ className }) => (!isDefaultLogo && logoUrl)
    ? <img loading="eager" decoding="async" fetchPriority="high" src={logoUrl} alt={siteName || ''} className={className} />
    : <span className="font-bold text-xl text-emerald-900">{siteName}</span>;

  return (
    <header
      style={{ top: bannerHeight }}
      className={`md:hidden fixed left-0 right-0 z-50 bg-white backdrop-blur-lg border-b border-slate-200 transition-transform duration-300 h-[56px] ${navbarVisible ? 'translate-y-0' : '-translate-y-full'}`}
    >
      <div className="flex items-center justify-between px-3 h-full">
        <Link to="/" data-testid="mobile-logo-link"><LogoImg className="h-9 w-auto" /></Link>

        <div className="flex items-center gap-1">
          {/* Province Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-stone-100 shrink-0" data-testid="mobile-province-selector">
                <MapPin className="w-3.5 h-3.5 text-orange-500" />
                <span className="max-w-[70px] truncate">{selectedProvince?.name || 'All UK'}</span>
                <ChevronDown className="w-3 h-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-h-72 overflow-y-auto">
              <DropdownMenuItem onClick={() => setSelectedProvince(null)} className={`cursor-pointer ${!selectedProvince ? 'bg-orange-50 text-orange-700 font-semibold' : ''}`} data-testid="mobile-province-all">
                <MapPin className="w-4 h-4 mr-2" />All UK
              </DropdownMenuItem>
              {provinces.map(prov => (
                <DropdownMenuItem key={prov.region_id} onClick={() => setSelectedProvince(prov)} className={`cursor-pointer ${selectedProvince?.region_id === prov.region_id ? 'bg-orange-50 text-orange-700 font-semibold' : ''}`} data-testid={`mobile-province-${prov.plate_code}`}>
                  <MapPin className="w-4 h-4 mr-2" />{prov.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user && <NotificationBell user={user} />}

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] overflow-y-auto p-0 [&>button]:hidden">
              <div className="flex flex-col pb-8">
                {user ? (
                  <>
                    {/* User Info Header */}
                    <div className="bg-gradient-to-r from-emerald-900 to-emerald-800 p-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-14 w-14 border-2 border-white/30"><AvatarImage src={getUserAvatarUrl(user)} /><AvatarFallback className="bg-white/20 text-white text-lg">{user.name?.charAt(0)}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-semibold text-white">{user.name}</p>
                          <p className="text-xs text-white/70">{user.email}</p>
                          <Badge className="mt-1 bg-white/20 text-white text-xs">
                            {formatUserRoleLabel(user)}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* My Account */}
                    <div className="px-4 pt-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-3">My Account</p>
                      <nav className="flex flex-col gap-1">
                        {[
                          { to: '/provider-dashboard', icon: Home, label: 'Dashboard' },
                          { to: '/my-listings', icon: FileText, label: 'My Listings' },
                          { to: '/my-requests', icon: ClipboardList, label: 'My Requests' },
                          { to: '/my-purchases', icon: ShoppingCart, label: 'Purchases' },
                          { to: '/favourites', icon: Heart, label: 'Favourites' },
                        ].map(item => (
                          <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100">
                            <item.icon className="w-5 h-5 text-slate-500" />{item.label}
                          </Link>
                        ))}
                        <Link to="/messages" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100">
                          <MessageCircle className="w-5 h-5 text-slate-500" />Messages
                          {unreadMessages > 0 && <Badge className="ml-auto bg-emerald-600">{unreadMessages}</Badge>}
                        </Link>
                      </nav>
                    </div>

                    {/* Settings */}
                    <div className="px-4 pt-4 border-t mt-2">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-3">Settings</p>
                      <nav className="flex flex-col gap-1">
                        <Link to="/my-documents" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100"><FileText className="w-5 h-5 text-slate-500" />My Documents</Link>
                        <Link to="/packages" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-slate-100"><Package className="w-5 h-5 text-slate-500" />Membership Plans</Link>
                      </nav>
                    </div>

                    {/* Professional Panels */}
                    {(user.role === 'provider' || user.role === 'admin' || user.role === 'superadmin') && (
                      <div className="px-4 pt-4 border-t mt-2"><Link to="/provider-dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-emerald-600 hover:bg-emerald-50 font-medium"><Briefcase className="w-5 h-5" />Provider Panel</Link></div>
                    )}
                    {user.is_realtor && (
                      <div className="px-4 pt-2"><Link to="/agent-dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-teal-600 hover:bg-teal-50 font-medium" data-testid="mobile-realtor-panel"><Building2 className="w-5 h-5" />Estate Agent Panel</Link></div>
                    )}
                    {user.is_gallery_dealer && (
                      <div className="px-4 pt-2"><Link to="/dealer-dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-emerald-700 hover:bg-emerald-50 font-medium" data-testid="mobile-gallery-dealer-panel"><Car className="w-5 h-5" />Dealer Panel</Link></div>
                    )}
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <div className="px-4 pt-2"><Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[#f97316] font-medium hover:bg-orange-50"><Settings className="w-5 h-5" />Admin Panel</Link></div>
                    )}

                    <div className="px-4 pt-4 mt-2 border-t">
                      <Button variant="ghost" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full justify-start text-red-600 hover:bg-red-50 px-3"><LogOut className="w-5 h-5 mr-3" />Sign Out</Button>
                    </div>
                  </>
                ) : (
                  <div className="p-4">
                    <Button onClick={handleLogin} className="btn-primary w-full" data-testid="mobile-login-btn">Sign In</Button>
                    <nav className="flex flex-col gap-1 mt-4">
                      {navItems.map((item) => (
                        <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isActive(item.href) ? 'bg-emerald-900/10 text-emerald-900' : 'text-stone-600 hover:bg-stone-100'}`}>
                          <item.icon className="w-5 h-5" />{item.label}
                        </Link>
                      ))}
                    </nav>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
